import { useMutation, useQueryClient } from "@tanstack/react-query";
import { adpRunIntegrationContract } from "@common/contracts/adp-run-integration.contract";
import { integrationConnectionWithAccountsResponseSchema } from "@common/schemas/domains/integrationConnection/integration-connection-response.schema";
import { AdpRunCreateConnectionBody } from "@common/schemas/domains/adpRun/adp-run-connection.schema";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { addIntegrationConnectionInQueryData } from "@queries/entitiesAccounts";
import { invalidateAccountRelatedQueries } from "./account.api";
import { integrationConnectionQueryKeys } from "./integrationConnection.api";

const adpRunContractClient = getBackendAPIClient(adpRunIntegrationContract);

interface CreateConnectionResult {
    connection: IntegrationConnection;
    isExisting: boolean;
}

export function useAdpRunConnectionCreationMutation(
    onSuccess?: (connection: IntegrationConnection) => void,
) {
    const { workspaceId } = useConnectTarget();
    const { updateConnectAccountState } = useConnectAccount();
    const { toast } = useToaster();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: Omit<AdpRunCreateConnectionBody, "workspaceId">,
        ): Promise<CreateConnectionResult> => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }
            const response = await adpRunContractClient.createConnection({
                body: { ...data, workspaceId },
            });
            return {
                connection:
                    integrationConnectionWithAccountsResponseSchema.parse(
                        response.body,
                    ),
                isExisting: response.status === 200,
            };
        },
        onMutate: () => {
            updateConnectAccountState({ isConnecting: true });
        },
        onSuccess: ({ connection, isExisting }) => {
            addIntegrationConnectionInQueryData(queryClient, connection);
            const hasAccountsNotReadyForSync =
                connection.integrationAccounts?.some(
                    (account) => !account.financialAccount?.entityId,
                );

            if (hasAccountsNotReadyForSync) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved: true,
                    integrationConnection: connection,
                });
            } else {
                updateConnectAccountState({ isConnecting: false });
                toast(
                    isExisting
                        ? "This ADP RUN company is already connected"
                        : "ADP RUN connected",
                );
            }

            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.query(
                    workspaceId ?? "",
                ),
            });
            invalidateAccountRelatedQueries({ workspaceId });
            onSuccess?.(connection);
        },
        onError: () => {
            updateConnectAccountState({ isConnecting: false });
        },
    });
}
